/**
 * User-facing guidance for `sdk_audioVideoDidStart_timeout` and
 * `sdk_audioVideoDidStart_failed` errors emitted from the WebRTC handshake
 * stage of `joinChimeSession`. The error indicates the SDK couldn't
 * establish (timeout) or kept the session alive (failed) before the join
 * page exits the spinner.
 *
 * The 2026-05-08 join-failure cluster (MIN-596) produced 57 such timeout
 * events in 24h across users on Safari 26.x / Chrome 14x / Firefox 150 /
 * iOS 26.2 — the "newer browser with stricter DTLS validation since
 * 2025" population that `docs/video-architecture.md` §4 line 308 names.
 * The documented escape hatch for these users is **switching browsers**:
 *   - Matias-Jofre: Safari 26.4.0 (3 timeouts) → Safari 26.3.1 (success in 2s)
 *   - Galit Aloni:  Safari 26.5.0 (4 timeouts) → Firefox 150     (success in 1s)
 *   - Gesche Wuerfel: Safari 26.4.0 (3 timeouts) → Firefox 149   (success in 2s)
 *
 * Pre-this-utility, `useJoinVideoMeeting` surfaced the SDK error message
 * verbatim ("audioVideoDidStart did not fire within 30000ms") which gives
 * the user no actionable next step. Sustained-failure users like Bill
 * Gaskins (8 timeouts on Safari 26.4 / macOS, never joined) had no
 * guidance at all to try a different browser.
 *
 * Mirrors the shape of `getDeviceStartErrorGuidance` for consistency:
 * `{ title, summary, causes[] }` consumed by the same access-error
 * surface that handles device-start failures.
 */

export type JoinFailureGuidanceArgs =
  | {
      kind: "timeout";
      // Timeout budget that elapsed (default 30_000ms post-MIN-596).
      timeoutMs: number;
      // True when this attempt followed a prior join attempt in the same
      // tab session. Pre-MIN-591 this was the smoking-gun for Population B
      // (attendee-leak); post-MIN-591 it should be near-zero in production.
      isRejoin: boolean;
    }
  | {
      kind: "failed";
      // The MeetingSessionStatusCode enum NAME from the SDK, e.g. "Left",
      // "AudioJoinedFromAnotherDevice". Carried for telemetry; not surfaced
      // to the user since the codes are too SDK-specific to be helpful.
      statusCode: string;
      isRejoin: boolean;
    };

export type JoinFailureGuidance = {
  title: string;
  summary: string;
  causes: string[];
};

const BROWSER_SWITCH_CAUSES = [
  "Try a different browser — switching to Firefox or Chrome resolves this for most users",
  "Check that your internet connection is stable. Corporate or VPN networks sometimes block video calls",
  "Refresh this page and try Join again",
];

export function getJoinFailureGuidance(
  args: JoinFailureGuidanceArgs
): JoinFailureGuidance {
  if (args.kind === "timeout") {
    const seconds = Math.round(args.timeoutMs / 1000);
    return {
      title: "Couldn't connect to the meeting",
      summary: `The video connection didn't complete in ${seconds} seconds. This sometimes happens on certain network and browser combinations.`,
      causes: BROWSER_SWITCH_CAUSES,
    };
  }
  // kind === "failed" — terminal `audioVideoDidStop` fired before
  // `audioVideoDidStart`. The SDK gave up unilaterally; same user
  // remediation works (browser switch / network check / retry).
  return {
    title: "Couldn't connect to the meeting",
    summary:
      "The video connection ended before it could fully start. This sometimes happens on certain network and browser combinations.",
    causes: BROWSER_SWITCH_CAUSES,
  };
}
